<template>
  <div class="contract-settings contract-settings-ged">
    <div v-if="origin === 'single_tool'" class="alert alert-info">
      Pour obtenir un taux net sécurisé, nous vous recommandons d'utiliser dans la simulation le maximum de jours
      travaillés possibles dans un mois. Cela permet de vérifier que le taux net reste sous le plafond de {{ toMoney(maxNetHelp) }} net,
      même lors des mois les plus chargés. Par exemple pour 5 jours de garde par semaine noter 22 jours.
    </div>

    <RadioGroup
      v-if="origin === 'single_tool'"
      :model-value="monthWeek"
      label="Saisie des infos"
      :options="monthWeekOptions"
      name="month_week"
      @update:model-value="$emit('update:monthWeek', $event)"
    />

    <div v-if="isMonth" class="overtime-container">
      <ToggleInput
        :model-value="hasOvertime"
        label="Heures majorées&nbsp;?"
        input-class="planned_overtime"
        @update:model-value="$emit('update:hasOvertime', $event)"
      />
    </div>

    <div v-if="monthWeek !== 'other'" class="month-datas">
      <div v-if="isMonth">
        <DecimalInput
          :model-value="monthlyWorkedDays"
          label="Jours de garde mensualisés"
          append="jours"
          :precision="4"
          @update:model-value="$emit('update:monthlyWorkedDays', $event)"
        >
          <template #hint>
            <Popup :icon="iconQuestion" :body="'<p>Le nombre mensualisé de jours pendant lesquels votre enfant est gardé.</p>'" />
          </template>
          <template #error>
            <div v-if="!isMonthlyWorkedDaysValid" class="invalid-feedback error">Valeur incorrecte</div>
          </template>
        </DecimalInput>
      </div>

      <div v-if="isMonth">
        <DecimalInput
          :model-value="monthlyHours"
          label="Heures de garde mensualisées"
          append="heures"
          :precision="4"
          @update:model-value="$emit('update:monthlyHours', $event)"
        >
          <template #hint>
            <Popup :icon="iconQuestion" :body="`<p>Le volume horaire <b>mensualisé</b> prévu au contrat.</p><p>Ce chiffre est obtenu en effectuant la mensualisation de vos différents plannings de garde.</p>`" />
          </template>
          <template #error>
            <div v-if="!isMonthlyHoursValid" class="invalid-feedback error">Valeur incorrecte</div>
          </template>
        </DecimalInput>
      </div>

      <div v-if="isWeek">
        <DecimalInput
          :model-value="weeklyWorkedDays"
          label="Jours par semaine"
          append="jours"
          :precision="2"
          @update:model-value="$emit('update:weeklyWorkedDays', $event)"
        >
          <template #hint>
            <Popup :icon="iconQuestion" :body="'<p>Le nombre moyen de jours par semaine pendant lesquels votre enfant est gardé.</p>'" />
          </template>
          <template #error>
            <div v-if="!isWeeklyWorkedDaysValid" class="invalid-feedback error">Valeur incorrecte</div>
          </template>
        </DecimalInput>
      </div>

      <div v-if="isWeek">
        <DecimalInput
          :model-value="weeklyHours"
          label="Heures par semaine"
          append="heures"
          :precision="4"
          @update:model-value="$emit('update:weeklyHours', $event)"
        >
          <template #hint>
            <Popup :icon="iconQuestion" :body="'<p>Le volume horaire hebdomadaire moyen prévu au contrat.</p>'" />
          </template>
          <template #error>
            <div v-if="!isWeeklyHoursValid" class="invalid-feedback error">Valeur incorrecte</div>
          </template>
        </DecimalInput>

        <div v-if="weeklyHoursFloat > maxOvertimeFifty" class="alert alert-danger mt-2">
          Attention, le volume horaire saisi dépasse le maximum autorisé de 50 heures par semaine.
        </div>
        <div v-else-if="weeklyHoursFloat > maxOvertimeForty" class="alert alert-warning mt-2">
          Attention, le volume horaire saisi dépasse 48 heures par semaine.
        </div>
      </div>

      <div>
        <DecimalInput
          :model-value="yearlyWeeks"
          label="Semaines de garde / an"
          append="semaines"
          :precision="0"
          @update:model-value="$emit('update:yearlyWeeks', $event)"
        >
          <template #hint>
            <Popup :icon="iconQuestion" :body="'<p>Le nombre de semaines par an prévues au contrat.</p>'" />
          </template>
          <template #error>
            <div v-if="!isYearlyWeeksValid" class="invalid-feedback error">Valeur incorrecte</div>
          </template>
        </DecimalInput>
        <div
          v-if="contractEndsBeforeOneYear && schedulableWeeks && weeksWorked"
          class="label form-text mb-3"
          style="margin-top: -10px"
        >
          Contrat sur <b>{{ schedulableWeeks }} semaines</b>
          (dont <b>{{ weeksWorked }} de garde</b>).
          Équivalent annuel pour la simulation : <b>{{ yearlyWeeksFloat }} semaines/an</b>.
        </div>
        <div v-if="contractEndsBeforeOneYear && yearlyWeeksFloat > 46 && yearlyWeeksFloat < 52" class="label">
          Dans le cas d'un contrat <b>planifié sur moins d'un an</b>, le nombre de semaine travaillées est ramené
          à l'année entière, et peut donc dépasser 46 !
        </div>
      </div>

      <div v-if="isMonth && hasOvertime">
        <DecimalInput
          :model-value="monthlyOvertime"
          label="Combien d'heures majorées, au-delà des 40 hebdomadaire, par mois ?"
          append="heures"
          :precision="4"
          @update:model-value="$emit('update:monthlyOvertime', $event)"
        >
          <template #hint>
            <Popup :icon="iconQuestion" :body="overtimeFortyHelp" />
          </template>
          <template #error>
            <div v-if="!isMonthlyOvertimeValid" class="invalid-feedback error">Valeur incorrecte</div>
          </template>
        </DecimalInput>

        <SelectInput
          :model-value="increaseOvertime"
          label="Avec une majoration du salaire de :"
          :options="overtimeOptions"
          @update:model-value="$emit('update:increaseOvertime', $event)"
        />

        <DecimalInput
          :model-value="monthlyOvertimeFifty"
          label="Combien d'heures majorées, au-delà des 48 heures hebdomadaire, par mois ?"
          append="heures"
          :precision="4"
          @update:model-value="$emit('update:monthlyOvertimeFifty', $event)"
        >
          <template #hint>
            <Popup :icon="iconQuestion" :body="overtimeFiftyHelp" />
          </template>
        </DecimalInput>

        <SelectInput
          :model-value="increaseOvertimeFifty"
          label="Avec une majoration du salaire de :"
          :options="overtimeFiftyOptions"
          @update:model-value="$emit('update:increaseOvertimeFifty', $event)"
        />
      </div>

      <div v-if="isWeek && weeklyHoursFloat > maxNormalWorkingHours" class="weekly_overtime">
        <div class="row mb-3">
          <label class="col-sm-6 col-form-label bold">Heures maj. au-delà des 40h par semaine :</label>
          <div class="col-sm row mx-0 align-items-center">
            <div class="col form-control bold" readonly>{{ weeklyOvertimeFortyDisplay }}</div>
            <div class="col-2 form-text p-0 text-right">
              <Popup :icon="iconQuestion" :body="weeklyOvertimeFortyHelp" />
            </div>
          </div>
        </div>

        <SelectInput
          :model-value="increaseOvertime"
          label="Avec une majoration du salaire de :"
          :options="overtimeOptions"
          @update:model-value="$emit('update:increaseOvertime', $event)"
        />

        <div v-if="weeklyHoursFloat > maxOvertimeForty" class="row mb-3">
          <label class="col-sm-6 col-form-label bold">Heures maj. au-delà des 48h par semaine :</label>
          <div class="col-sm row mx-0 align-items-center">
            <div class="col form-control bold" readonly>{{ weeklyOvertimeFiftyDisplay }}</div>
            <div class="col-2 form-text p-0 text-right">
              <Popup :icon="iconQuestion" :body="weeklyOvertimeFiftyHelp" />
            </div>
          </div>
        </div>

        <SelectInput
          v-if="weeklyHoursFloat > maxOvertimeForty"
          :model-value="increaseOvertimeFifty"
          label="Avec une majoration du salaire de :"
          :options="overtimeFiftyOptions"
          @update:model-value="$emit('update:increaseOvertimeFifty', $event)"
        />
      </div>
    </div>

    <div v-if="monthWeek !== 'other'">
      <div id="hourly-rate-container" class="cmg-remuneration">
        <h6 class="cmg-remuneration__title mb-3">Rémunération</h6>

        <RangeSlider
          v-if="minimumGrossHourlyWage > 0"
          :model-value="grossHourlyWageFloat"
          :min="minimumGrossHourlyWage"
          :max="maximumGrossHourlyWage"
          :step="0.01"
          :format-tooltip="formatWageTooltip"
          @update:model-value="onSliderInput"
          @slider-ready="$emit('slider-ready', $event)"
        />

        <div class="cmg-remuneration__manual mt-4">
          <div class="text-center font--bold mb-1">
            Saisie manuelle du taux horaire brut
          </div>
          <div class="d-flex align-items-center justify-content-center gap-3">
            <button type="button" class="cmg-hourly-rate-btn" @click="$emit('adjustHourlyRate', 'decrease')" v-html="icons.iconSignMinus" />
            <div class="input-group" style="width: auto !important;">
              <input
                type="text"
                class="form-control"
                :value="grossHourlyWageInput"
                data-precision="2"
                @input="$event.target.value = $event.target.value.replace('.', ','); $emit('update:grossHourlyWageInput', $event.target.value); $emit('updateHourlyWage')"
              />
              <span class="input-group-text">€ (brut)</span>
            </div>
            <button type="button" class="cmg-hourly-rate-btn" @click="$emit('adjustHourlyRate', 'increase')" v-html="icons.iconSignPlus" />
          </div>
        </div>

        <div v-if="!isHourlyRateInRange" class="text-danger font--xs mt-2">
          La plage de valeurs recommandées est comprise entre {{ toFloatForDisplay(minimumGrossHourlyWage, 2) }} €
          et {{ maximumGrossHourlyWage }} €.
        </div>
        <div v-if="maxCMGHelpExceeded" class="alert alert-danger mt-4 font--xs">
          Avec la nouvelle reforme de Septembre 2025, le plafond d'aide est à {{ toMoney(maxNetHelp) }} / heure (net).
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent, computed } from 'vue';
import {
  DecimalInput,
  Popup,
  RadioGroup,
  RangeSlider,
  SelectInput,
  ToggleInput,
  iconQuestion,
} from '@twg/ui';
import { iconSignMinus, iconSignPlus } from '../icons';

export default defineComponent({
  name: 'ContractSettingsGed',
  components: { DecimalInput, Popup, RadioGroup, RangeSlider, SelectInput, ToggleInput },
  props: {
    monthWeek: { type: String, required: true },
    weeklyWorkedDays: { type: String, required: true },
    weeklyHours: { type: String, required: true },
    yearlyWeeks: { type: String, required: true },
    monthlyWorkedDays: { type: String, required: true },
    monthlyHours: { type: String, required: true },
    hasOvertime: { type: Boolean, required: true },
    monthlyOvertime: { type: String, required: true },
    monthlyOvertimeFifty: { type: String, required: true },
    increaseOvertime: { type: String, required: true },
    increaseOvertimeFifty: { type: String, required: true },
    maxNormalWorkingHours: { type: Number, default: 40 },
    maxOvertimeForty: { type: Number, default: 48 },
    maxOvertimeFifty: { type: Number, default: 50 },
    contractEndsBeforeOneYear: { type: Boolean, default: false },
    schedulableWeeks: { type: Number, default: null },
    weeksWorked: { type: Number, default: null },
    grossHourlyWageInput: { type: String, required: true },
    grossHourlyWageFloat: { type: Number, required: true },
    netRatioClassic: { type: Number, required: true },
    isHourlyRateInRange: { type: Boolean, default: true },
    minimumGrossHourlyWage: { type: Number, default: 10.35 },
    maximumGrossHourlyWage: { type: Number, default: 60 },
    maxCMGHelpExceeded: { type: Boolean, default: false },
    maxNetHelp: { type: Number, required: true },
    isMonthlyWorkedDaysValid: { type: Boolean, default: true },
    isMonthlyHoursValid: { type: Boolean, default: true },
    isWeeklyWorkedDaysValid: { type: Boolean, default: true },
    isWeeklyHoursValid: { type: Boolean, default: true },
    isYearlyWeeksValid: { type: Boolean, default: true },
    isMonthlyOvertimeValid: { type: Boolean, default: true },
    origin: { type: String, default: 'single_tool' },
    toFloatForDisplay: { type: Function, required: true },
    toMoney: { type: Function, required: true },
    toDecimal4: { type: Function, required: true },
  },
  emits: [
    'update:monthWeek', 'update:weeklyWorkedDays', 'update:weeklyHours',
    'update:yearlyWeeks', 'update:monthlyWorkedDays', 'update:monthlyHours',
    'update:hasOvertime', 'update:monthlyOvertime', 'update:monthlyOvertimeFifty',
    'update:increaseOvertime', 'update:increaseOvertimeFifty',
    'update:grossHourlyWageInput', 'adjustHourlyRate', 'updateHourlyWage',
    'slider-ready', 'setGrossHourlyWageFromSlider',
  ],
  setup(props, ctx) {
    const isMonth = computed(() => props.monthWeek === 'month');
    const isWeek = computed(() => props.monthWeek === 'week');
    const weeklyHoursFloat = computed(() => parseFloat(props.weeklyHours.replace(',', '.')));
    const yearlyWeeksFloat = computed(() => parseFloat(props.yearlyWeeks.replace(',', '.')));

    const weeklyOvertimeFortyDisplay = computed(() => {
      const aboveForty = weeklyHoursFloat.value > props.maxOvertimeForty
        ? props.maxOvertimeForty - props.maxNormalWorkingHours
        : weeklyHoursFloat.value - props.maxNormalWorkingHours;
      return props.toDecimal4(Math.max(aboveForty, 0));
    });

    const weeklyOvertimeFiftyDisplay = computed(() => {
      return props.toDecimal4(Math.max(weeklyHoursFloat.value - props.maxOvertimeForty, 0));
    });

    const monthWeekOptions = computed(() => {
      const options = [
        { label: 'au mois', value: 'month' },
        { label: 'à la semaine', value: 'week' },
      ];
      if (props.origin !== 'contract_editor') {
        options.push({ label: 'autre', value: 'other' });
      }
      return options;
    });

    // CCN GED floors: 25 % minimum for 40–48h overtime, 50 % minimum for 48h+
    // (Vue 2 offered 25–100 % and 50–200 % — assmat's 10–50 % list does not apply).
    const overtimeOptions = Array.from({ length: 16 }, (_, i) => ({
      label: `${(i + 5) * 5} %`,
      value: String((i + 5) * 5),
    }));

    const overtimeFiftyOptions = Array.from({ length: 31 }, (_, i) => ({
      label: `${(i + 10) * 5} %`,
      value: String((i + 10) * 5),
    }));

    const icons = { iconSignMinus, iconSignPlus };

    const overtimeFortyHelp = '<p>Indiquez ici le volume horaire <b>mensualisé</b> des heures majorées au-delà de 40 heures et en dessous de 48 heures par semaine.</p>';
    const overtimeFiftyHelp = '<p>Indiquez ici le volume horaire <b>mensualisé</b> des heures majorées au-delà de 48 heures par semaine.</p>';
    const weeklyOvertimeFortyHelp = '<p>Les heures travaillées au-delà de 40 heures et en dessous de 48 heures par semaine. Elles sont obligatoirement majorées a minima de 25 %.</p>';
    const weeklyOvertimeFiftyHelp = '<p>Les heures travaillées au-delà de 48 heures par semaine. Elles sont obligatoirement majorées a minima à 50 %.</p>';

    function formatWageTooltip(value) {
      const grossLabel = props.toMoney(value);
      const netLabel = props.toMoney(value * props.netRatioClassic);
      return `<b>${grossLabel}</b><br /><span class="font--xs">(${netLabel} net)</span>`;
    }

    function onSliderInput(numericValue) {
      ctx.emit('setGrossHourlyWageFromSlider', numericValue);
    }

    return {
      isMonth,
      isWeek,
      weeklyHoursFloat,
      yearlyWeeksFloat,
      weeklyOvertimeFortyDisplay,
      weeklyOvertimeFiftyDisplay,
      monthWeekOptions,
      overtimeOptions,
      overtimeFiftyOptions,
      icons,
      iconQuestion,
      overtimeFortyHelp,
      overtimeFiftyHelp,
      weeklyOvertimeFortyHelp,
      weeklyOvertimeFiftyHelp,
      formatWageTooltip,
      onSliderInput,
    };
  },
});
</script>
